import { ReactNode } from 'react'

import { ClassNames, css, Theme, useTheme } from '@emotion/react'
import { ClassNames as DayPickerClassNames } from 'react-day-picker'

import { spacing } from '../../../theme'
import { useDropdownStatus } from './DatePickerDropdownContext'

type Props = {
  children: (classNames: DayPickerClassNames) => ReactNode
}

export const buttonLikeBase = (theme: Theme) => css`
  ${theme.typography.body};
  color: ${theme.arcadeColors.semantic.textStandard};
  background: ${theme.arcadeColors.semantic.backgroundSubtle};
  border-radius: ${theme.border.radius.small};
  border: ${theme.pxToRem(2)} solid transparent;
  display: grid;
  place-items: center;
  cursor: pointer;
  outline: 0;
`

export const buttonLikeActive = (theme: Theme) => css`
  background: ${theme.arcadeColors.semantic.backgroundStandard};
`

export const buttonLikeSelected = (theme: Theme) => css`
  border: ${theme.pxToRem(2)} solid ${theme.arcadeColors.semantic.borderProminent};
  border-radius: ${theme.border.radius.small};
  font-weight: ${theme.weights.medium}; /* stylelint-disable-line @cash-web/no-hardcoded-font-styles */
  background: none;
`

export const buttonLikeFocus = (theme: Theme) => css`
  outline-style: solid;
  outline-width: 0.5rem;
  outline-color: ${theme.arcadeColors.semantic.borderProminent}33;
`

const smallButtonMotion = (theme: Theme) => css`
  ${theme.motion.standardTransition};
  transition-property: transform, opacity;
  transform-origin: center center;

  @media (hover: hover) {
    :hover {
      opacity: 0.75;
    }
  }

  :active {
    opacity: 0.5;
    ${theme.motion.standardSubtleActiveState};
  }
`

export const visuallyHidden = css`
  border: 0;
  clip: rect(0 0 0 0);
  height: auto;
  margin: 0;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
  white-space: nowrap;
`

export const ArcadeDatePickerClassNames = ({ children }: Props) => {
  const theme = useTheme()
  const dropdownStatus = useDropdownStatus()

  const dayInnerSize = '1.5rem'
  const daySize = '2.5rem'

  return (
    <ClassNames>
      {({ css }) =>
        children({
          button: css``,
          day: css`
            ${buttonLikeBase(theme)}
            background: none;
            position: absolute;
            width: ${daySize};
            height: ${daySize};

            ${theme.selectors.keyboard} &:focus {
              ${buttonLikeFocus(theme)}
            }

            ${smallButtonMotion(theme)}

            :disabled {
              color: ${theme.arcadeColors.semantic.textDisabled};
              cursor: default;
              transition: none;
            }
          `,
          day_selected: css`
            /* increase specificity to always be greater than above "day" */
            body & {
              ${buttonLikeSelected(theme)}
              color: ${theme.arcadeColors.semantic.textProminent};
              transition-property: transform;
            }
          `,
          day_today: css`
            font-weight: ${theme.weights.medium}; /* stylelint-disable-line @cash-web/no-hardcoded-font-styles */
          `,
          head_cell: css`
            ${theme.typography.identifier};
            text-align: center;
            vertical-align: top;
            color: ${theme.arcadeColors.semantic.textSubtle};
            width: ${dayInnerSize};
          `,
          head: css`
            display: grid;
          `,
          head_row: css`
            display: flex;
            justify-content: space-between;
          `,
          tbody: css`
            display: grid;
            gap: ${spacing.m};
          `,
          cell: css`
            position: relative;
            width: ${dayInnerSize};
            height: ${dayInnerSize};
            display: flex;
            align-items: center;
            justify-content: center;
          `,
          row: css`
            display: flex;
            justify-content: space-between;
          `,
          caption: css`
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: ${theme.spacing.s};
            align-items: center;
            justify-content: space-between;
            margin-bottom: ${theme.spacing.l};
          `,
          caption_label: css`
            ${visuallyHidden}
          `,
          caption_dropdowns: css`
            grid-column: span 3; /* align exactly with three dropdown options */
            width: 100%;
            display: flex;
          `,
          nav: css`
            display: flex;
            gap: calc(${theme.spacing.xxs} / 2);
            margin-left: calc(${theme.spacing.s} * -1); /* erase grid colum gap */
            padding-left: ${spacing.xxs};
            justify-content: space-between;
          `,
          nav_button: css`
            color: ${theme.arcadeColors.semantic.iconSubtle};
            background: none;
            border: none;
            padding: 0;
            width: 2rem;
            height: 2rem;
            cursor: pointer;

            ${smallButtonMotion(theme)}

            :disabled {
              color: ${theme.arcadeColors.semantic.iconDisabled};
              cursor: default;
              transition: none;
            }
          `,
          nav_button_next: css`
            rotate: 90deg;
          `,
          nav_button_previous: css`
            rotate: -90deg;
          `,
          table: css`
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: ${spacing.m};

            ${dropdownStatus.open &&
            // hides table while preserving width
            css`
              display: grid;
              grid-auto-rows: 0;
              gap: 0;
              visibility: hidden;
              margin-bottom: 0;
            `}
          `,
        })
      }
    </ClassNames>
  )
}
